<script setup lang="ts">
import { generateDisplayName } from '@/utils/user'
import { computed, ref } from 'vue'
import { useOrgStore } from '@/stores/org'
import { useAccessStore } from '@/stores/access'

const emit = defineEmits<{ (e: 'invited'): void }>()

import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { PlusCircle } from 'lucide-vue-next'
import { toast } from '@/components/ui/toast'
import MemberSearchInput from '@/components/member/MemberSearchInput.vue'
import MemberInviteList, { missingName } from '@/components/member/MemberInviteList.vue'
import MemberManualEntryForm from '@/components/member/MemberManualEntryForm.vue'
import type { SearchMember } from '@/components/member/MemberSearchInput.vue'
import type { InviteRowItem, RoleOption } from '@/components/member/MemberInviteList.vue'
import type { ManualEntryValues } from '@/components/member/MemberManualEntryForm.vue'

import { Org } from '@/types'

const orgStore = useOrgStore()

const accessStore = useAccessStore()

const ORG_ROLES = computed<RoleOption[]>(() => accessStore.roleChoices('org'))
const defaultRoleId = computed<number | null>(() => accessStore.seededRoleId('org_member'))

const props = defineProps<{
  org: Org
}>()

const isOpen = ref(false)
const inviteRows = ref<InviteRowItem[]>([])
const showManualEntry = ref(false)
const manualRole = ref<string | number>('')
const manualInitialContact = ref('')

const isSubmitting = ref(false)

const open = () => {
  isOpen.value = true
  inviteRows.value = []
  showManualEntry.value = false
  manualRole.value = defaultRoleId.value ?? ''
  manualInitialContact.value = ''
}

const close = () => {
  isOpen.value = false
}

const onSelectMember = (member: SearchMember) => {
  if (inviteRows.value.some(r => r.member_id === member.member_id)) {
    toast({ title: 'Member already added', variant: 'destructive' })
    return
  }

  inviteRows.value.push({
    member_id: member.member_id,
    first_name: member.first_name || '',
    last_name: member.last_name || '',
    email: member.email || '',
    phone: member.phone || null,
    avatar_url: member.avatar_url || null,
    display_name: member.display_name || null,
    role: defaultRoleId.value ?? '',
    verified: member.verified,
  })
}

const onAddNew = (contact = '') => {
  manualInitialContact.value = contact
  showManualEntry.value = true
}

const onManualSubmit = (values: ManualEntryValues, role: string | number) => {
  if (inviteRows.value.some(r => r.email === values.email)) {
    toast({ title: 'This email is already added', variant: 'destructive' })
    return
  }

  inviteRows.value.push({
    member_id: null,
    first_name: values.first_name,
    last_name: values.last_name,
    email: values.email,
    phone: null,
    avatar_url: null,
    display_name: generateDisplayName(values.first_name, values.last_name),
    role,
  })

  showManualEntry.value = false
  manualRole.value = defaultRoleId.value ?? ''
  manualInitialContact.value = ''
}

const onManualCancel = () => {
  showManualEntry.value = false
  manualInitialContact.value = ''
}

const removeRow = (index: number) => {
  inviteRows.value.splice(index, 1)
}

const updateRole = (index: number, role: string | number) => {
  inviteRows.value[index].role = role
}

const onSubmit = async () => {
  if (inviteRows.value.length === 0) return

  isSubmitting.value = true
  try {
    const failedRows: InviteRowItem[] = []
    for (const row of inviteRows.value) {
      const success = await orgStore.invite({
        role_id: row.role,
        email: row.email,
        first_name: row.first_name,
        last_name: row.last_name,
        fullname: row.display_name || generateDisplayName(row.first_name || '', row.last_name || ''),
        organization_id: props.org.organization_id,
      })
      if (!success) failedRows.push(row)
    }
    if (failedRows.length > 0) {
      inviteRows.value = failedRows
    } else {
      close()
      emit('invited')
    }
  } finally {
    isSubmitting.value = false
  }
}

defineExpose({
  open
})
</script>

<template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogTrigger>
      <slot>
        <Button class="px-2" variant="ghost">
          <PlusCircle class="text-gray-500" />
        </Button>
      </slot>
    </DialogTrigger>
    <DialogContent class="max-w-[450px]">
      <DialogHeader>
        <DialogTitle>Invite users to organization</DialogTitle>
        <DialogDescription></DialogDescription>
      </DialogHeader>

      <div class="space-y-4">
        <div class="flex w-full items-center gap-2">
          <div class="flex-1 min-w-0">
            <MemberSearchInput
              :org-id="org.organization_id"
              :hide-add-new="true"
              @select="onSelectMember"
              @add-new="onAddNew"
            />
          </div>
          <Button variant="secondary" class="shrink-0 whitespace-nowrap" @click="() => onAddNew()">
            Add New User
          </Button>
        </div>

        <MemberManualEntryForm
          v-if="showManualEntry"
          v-model:role="manualRole"
          :roles="ORG_ROLES"
          :default-role="defaultRoleId ?? ''"
          mode="email"
          :initial-contact="manualInitialContact"
          @submit="onManualSubmit"
          @cancel="onManualCancel"
        />

        <div v-if="inviteRows.length > 0">
          <p class="text-base font-semibold mb-2">Invitation List</p>
        </div>

        <div class="max-h-[300px] overflow-y-auto">
          <MemberInviteList
            require-names
            :rows="inviteRows"
            :roles="ORG_ROLES"
            @remove="removeRow"
            @update:role="updateRole"
          />
        </div>

        <DialogFooter>
          <Button @click="onSubmit" :disabled="inviteRows.length === 0 || inviteRows.some(r => !r.role || missingName(r)) || isSubmitting">
            {{ isSubmitting ? 'Sending...' : `Invite (${inviteRows.length})` }}
          </Button>
        </DialogFooter>
      </div>
    </DialogContent>
  </Dialog>
</template>
